import { motion, useScroll, useSpring } from 'framer-motion';
import { useEffect, useState } from 'react';
import { type PitchSectionDef } from './sections';

/**
 * Sticky top-of-page progress strip.
 *
 * - 2px line at the very top that fills as the user scrolls the document.
 * - Faint section dot rail below it (desktop only) — each dot represents a
 *   pitch section, the active one is filled brand-bright.
 * - Click any dot to jump to that section.
 *
 * Deliberately minimal: this is investor-grade, not app-chrome.
 */
export function PitchProgress({ sections }: { sections: PitchSectionDef[] }) {
  const { scrollYProgress } = useScroll();
  const smooth = useSpring(scrollYProgress, {
    stiffness: 110,
    damping: 24,
    mass: 0.4,
  });

  const activeId = useActiveSection(sections);

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-40">
      {/* Top fill bar */}
      <div className="h-[2px] w-full bg-border-subtle">
        <motion.div
          className="h-full origin-left bg-brand-bright"
          style={{ scaleX: smooth }}
        />
      </div>

      {/* Section dot rail — desktop only, subtle */}
      <nav
        aria-label="Pitch sections"
        className="pointer-events-auto mx-auto hidden max-w-6xl items-center justify-end gap-2 px-10 py-4 lg:flex"
      >
        {sections.map((s) => {
          const isActive = s.id === activeId;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              aria-label={`Jump to ${s.label}`}
              aria-current={isActive ? 'true' : undefined}
              className="group relative flex items-center gap-2 px-2 py-1.5"
            >
              <span
                className={
                  'block h-1.5 w-1.5 rounded-full transition-all duration-300 ' +
                  (isActive
                    ? 'bg-brand-bright shadow-[0_0_12px_rgba(96,165,250,0.6)]'
                    : 'bg-border-strong group-hover:bg-ink-muted')
                }
              />
              <span
                className={
                  'overflow-hidden text-[11px] font-medium uppercase tracking-[0.18em] transition-all duration-300 ' +
                  (isActive
                    ? 'max-w-[140px] text-ink-strong opacity-100'
                    : 'max-w-0 text-ink-muted opacity-0 group-hover:max-w-[140px] group-hover:opacity-100')
                }
              >
                {s.label}
              </span>
            </a>
          );
        })}
      </nav>
    </div>
  );
}

/**
 * IntersectionObserver-based "which section am I in".
 * Picks the first section whose top is in the upper half of the viewport.
 */
function useActiveSection(sections: PitchSectionDef[]): string | null {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Among currently-intersecting sections, pick the one closest to the
        // top of the viewport (smallest positive top).
        const visible = entries
          .filter((e) => e.isIntersecting)
          .map((e) => ({
            id: e.target.id,
            top: e.boundingClientRect.top,
          }))
          .sort((a, b) => Math.abs(a.top) - Math.abs(b.top));
        if (visible[0]) setActive(visible[0].id);
      },
      // Trigger when a section crosses the upper third of the viewport.
      { rootMargin: '-30% 0px -55% 0px', threshold: 0 },
    );

    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  return active;
}
